/**
 * @flow
 */

import React from 'react';
import gql from 'graphql-tag';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import {
    Container,
    Menu as BaseMenu,
    Statistic,
    Divider,
    Select as BaseSelect,
} from 'semantic-ui-react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { isEmpty, map, get } from 'lodash';
import { format } from 'date-fns';
import { PageHeader, Table } from '../../../components';
import { filterByPromoCode } from '../../../actions/statistics';

export const GET_PROMO_CODE_USERS = gql`
    query getPromoCodeUsers($resolveRole: String, $promoCodeFilter: [String]) {
        viewer(resolveRole: $resolveRole) {
            id
            ... on Sysadmin {
                promoCodes
                promoCodeUsers(promoCodeFilter: $promoCodeFilter) {
                    id
                    firstName
                    lastName
                    phone
                    createdAt
                    promoCode
                }
            }
        }
    }
`;

type PromoCodePageProps = { users: any };

const Menu = styled(BaseMenu)`
    &.menu {
        margin: -0.5em -1em !important;
    }
`;

const Select = styled(BaseSelect)`
    min-width: 300px !important;
`;

const EmptyMessage = () => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={4} textAlign="center">
            <FormattedMessage
                id="pages.administrator.statistics.noPromoCodeUsers"
                defaultMessage="You don't have promo code user yet"
            />
        </Table.Cell>
    </Table.Row>
);

const UserRow = ({ user }: UserRowProps) => (
    <Table.Row>
        <Table.Cell>{user.firstName}</Table.Cell>
        <Table.Cell>{user.lastName}</Table.Cell>
        <Table.Cell>{user.phone}</Table.Cell>
        <Table.Cell>{format(new Date(user.createdAt), 'dd.MM.yyyy')}</Table.Cell>
    </Table.Row>
);

const PromoCodePage = (props: PromoCodePageProps) => {
    const dispatch = useDispatch();
    const promoCodeFilter = useSelector(({ statistics }) =>
        statistics.get('promoCodeFilter'),
    );

    const { data } = useQuery(GET_PROMO_CODE_USERS, {
        variables: {
            resolveRole: 'SYSADMIN',
            promoCodeFilter: promoCodeFilter || [],
        },
        fetchPolicy: 'cache-and-network',
    });

    const viewer = data && data.viewer;
    const users = get(viewer, 'promoCodeUsers', {});
    const promoCodeOptions = map(get(viewer, 'promoCodes', {}), item => ({
        value: item,
        text: item,
    }));

    const onPromoCodeSelect = (e, { value }) => {
        dispatch(filterByPromoCode(value));
    };

    return (
        <Container fluid>
            <Menu secondary>
                <BaseMenu.Item header>
                    <PageHeader>
                        <FormattedMessage
                            id="pages.administrator.statistics.promoCode"
                            defaultMessage="Promo codes"
                        />
                    </PageHeader>
                </BaseMenu.Item>
                <BaseMenu.Item position="left">
                    <Select
                        defaultValue={promoCodeFilter}
                        options={promoCodeOptions}
                        onChange={onPromoCodeSelect}
                        search
                        multiple
                    />
                </BaseMenu.Item>
                <BaseMenu.Item position="right">
                    <Statistic>
                        <Statistic.Label>
                            <FormattedMessage
                                id="pages.administrator.statistics.totalRecords"
                                defaultMessage="Total records"
                            />
                        </Statistic.Label>
                        <Statistic.Value>{users.length}</Statistic.Value>
                    </Statistic>
                </BaseMenu.Item>
            </Menu>
            <Divider />
            <Table celled selectable striped fixed>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>
                            <FormattedMessage
                                id="pages.administrator.statistics.firstName"
                                defaultMessage="First name"
                            />
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            <FormattedMessage
                                id="pages.administrator.statistics.lastName"
                                defaultMessage="Last name"
                            />
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            <FormattedMessage
                                id="pages.administrator.statistics.phone"
                                defaultMessage="Phone"
                            />
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            <FormattedMessage
                                id="pages.administrator.statistics.registrationDate"
                                defaultMessage="Registration date"
                            />
                        </Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {isEmpty(users) ? (
                        <EmptyMessage />
                    ) : (
                        users.map(user => <UserRow key={user.id} user={user} />)
                    )}
                </Table.Body>
            </Table>
            <br />
            <br />
            <br />
        </Container>
    );
};

export default injectIntl(PromoCodePage);
